import { useState, useEffect } from "react";
import { post } from "../../utils/api";
import { useDebounce } from "../hooks/useDebounce";

const ItineraryPicker = ({ selectedItems, onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debouncedQuery = useDebounce(query, 400);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const search = async () => {
      setSearching(true);
      try {
        const res = await post("/cms/public-itineraries/search", {
          query: debouncedQuery,
          page: 1,
          limit: 10,
        });
        if (!cancelled && res.status) setResults(res.data?.data || []);
      } finally {
        if (!cancelled) setSearching(false);
      }
    };
    search();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const addItem = (itinerary) => {
    if (selectedItems.find((i) => i.itinerarydetailsid === itinerary.itinerarydetailsid)) return;
    onChange([...selectedItems, { ...itinerary, display_order: selectedItems.length }]);
    setQuery("");
    setResults([]);
  };

  const removeItem = (id) => {
    onChange(
      selectedItems
        .filter((i) => i.itinerarydetailsid !== id)
        .map((i, idx) => ({ ...i, display_order: idx })),
    );
  };

  const moveUp = (index) => {
    if (index === 0) return;
    const next = [...selectedItems];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next.map((i, idx) => ({ ...i, display_order: idx })));
  };

  const moveDown = (index) => {
    if (index === selectedItems.length - 1) return;
    const next = [...selectedItems];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange(next.map((i, idx) => ({ ...i, display_order: idx })));
  };

  return (
    <div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search public itineraries..."
        className="w-full h-[40px] border border-gray-300 rounded-md px-3 text-sm text-gray-700 mb-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {searching && <p className="text-xs text-gray-400 mb-2">Searching...</p>}
      {results.length > 0 && (
        <div className="border border-gray-200 rounded-md max-h-36 overflow-y-auto mb-3">
          {results.map((item) => (
            <div
              key={item.itinerarydetailsid}
              className="flex justify-between items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b last:border-0"
              onClick={() => addItem(item)}
            >
              <span className="text-gray-800">{item.itineraryname}</span>
              <span className="text-gray-400 text-xs ml-2 shrink-0">{item.destination}</span>
            </div>
          ))}
        </div>
      )}
      {selectedItems.length > 0 && (
        <div className="mt-2 border border-gray-200 rounded-md divide-y divide-gray-100 overflow-hidden">
          {selectedItems.map((item, index) => (
            <div
              key={item.itinerarydetailsid}
              className="flex items-center gap-2 px-2 py-1.5 bg-white hover:bg-gray-50 text-sm"
            >
              <span className="text-gray-300 text-xs w-4 shrink-0 text-center">{index + 1}</span>
              <span className="flex-1 text-gray-800 truncate">{item.itineraryname}</span>
              <span className="text-gray-400 text-xs shrink-0">{item.destination}</span>
              <div className="flex items-center gap-0.5 shrink-0 ml-1">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>arrow_upward</span>
                </button>
                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index === selectedItems.length - 1}
                  className="p-0.5 text-gray-400 hover:text-gray-700 disabled:opacity-20"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>arrow_downward</span>
                </button>
                <button
                  type="button"
                  onClick={() => removeItem(item.itinerarydetailsid)}
                  className="p-0.5 text-red-400 hover:text-red-600 ml-0.5"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 13 }}>close</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ItineraryPicker;
